import { genericClient } from 'usage-tracker-core/client';
import { lWindow } from 'usage-tracker-core/common/helpers';
import { createContainer } from 'usage-tracker-multi';
import { createHstcScheduler, getTrackerType } from './utils';
const containeredCreateTracker = () => {
  // if `usage-tracker-cdn` is loaded multiple times within a page context,
  // this ensures we only have one instance of it being defined throughout the window context
  // this also prevents multiple `createHstcScheduler` from being created
  if (typeof (lWindow === null || lWindow === void 0 ? void 0 : lWindow.createTracker) === 'function') {
    return;
  }

  // Creates an UsageTrackerMulti Container with a History Proxy for actual Browser usage
  // that does not involve HubSpot Bender-based History API
  // We also disable UUID storage on HubSpot.com
  const cdnContainer = createContainer({
    storeUuidOnUrl: false
  });

  // Creates a permanent store for the current Tracking Type
  const cdnConfig = {
    // Creates a fresh new Map for Tracker Instances
    trackers: new Map(),
    // Defines the current Tracker Type at the moment of window load
    type: getTrackerType(genericClient.getHstc())
  };

  // This is a proxied version of createTracker and what is actually exposed to the window object
  const createTracker = config => {
    if (config.trackerName && config.trackerName.length) {
      // Creates a new Tracker Instance with the provided Config
      const {
        track
      } = cdnContainer.createTracker(Object.assign({}, config, {
        // Retrieves the current Tracker Type from the CDN Config
        trackerType: cdnConfig.type,
        // The CDN Tracker is a standalone Tracker
        standalone: true
      }));

      // Stores the new Tracker Instance (track method) and supplied config to the CDN Config
      // so whenever we need to switch trackers automatically it works out of the box
      cdnConfig.trackers.set(config.trackerName, {
        config,
        instance: track
      });

      // We define the "proxied" track method, which pretty much just retrieves the Tracker "track" method
      // from the CDN Config Trackers Map, so that we always have the latest Tracker "track" method
      // from the updated instance. JavaScript OOM automatically clears the unused instance.
      const proxyTrack = (...args) => cdnConfig.trackers.get(config.trackerName).instance(...args);
      return {
        track: proxyTrack
      };
    }

    // We require a trackerName field to be provided to set on the Map
    throw new Error('"createTracker" requires a "trackerName" property');
  };

  // Creates a Scheduler for always keeping Tracking instances updated to if they should
  // either use anonymous or public tracking based on presence of Cookie Banner
  createHstcScheduler(hstc => {
    const newTrackerType = getTrackerType(hstc);

    // We only do something if the current tracker type is different from the new one
    if (newTrackerType !== cdnConfig.type) {
      cdnConfig.type = newTrackerType;

      // Updates all existing Tracker Instances Proactively
      cdnConfig.trackers.forEach(({
        config
      }) => {
        // This recreate the Tracker Instance automatically when the automatic Tracker Type
        // detection changes from public to anonymous or vice versa.
        // And allows us to preserve the current tracker instance state.
        // Keep in mind that state of global properties will be likely lost
        lWindow.createTracker(config);
      });
    }
  });

  // Exposes the createTracker method to the window context
  // if the lWindow object is available and can be injected into
  if (typeof lWindow === 'object') {
    lWindow.createTracker = createTracker;
  }
};

// Immediately invokes and exposes `globalThis.createTracker` to the window context
export default containeredCreateTracker();